import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { invalidateCampaignQueries } from '#/entities/campaign'
import { toCampaignCreate } from '#/features/campaign-enrol'
import type { NewCampaignInput } from '#/features/campaign-enrol'
import { useCreateCampaignApiV1CampaignsPost } from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { CAMPAIGN_DETAIL_TABS } from './detail-tabs'

/**
 * `POST /campaigns` — a draft, with no steps. On success the page moves to the
 * new campaign's Sequence tab, which is where the submit label promised to go.
 */
export function useCreateCampaign(onCreated?: () => void) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const create = useCreateCampaignApiV1CampaignsPost({
    mutation: {
      onSuccess: (response) => {
        invalidateCampaignQueries(queryClient)
        onCreated?.()
        void navigate({
          to: '/campaign/$campaignId',
          params: { campaignId: response.data.id },
          search: { tab: CAMPAIGN_DETAIL_TABS.SEQUENCE },
        })
      },
      onError: (error) =>
        toast.error(
          getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
        ),
    },
  })

  const onCreate = useCallback(
    (input: NewCampaignInput) =>
      create.mutate({ data: toCampaignCreate(input) }),
    [create],
  )

  return { onCreate, isCreating: create.isPending }
}
